/**
 * 启动性能标记（任务 20a）：用 User Timing 在应用内打点，e2e 与真机读同一组标记。
 *
 * 为什么要应用内打点，而不是在 e2e 里 `waitForSelector` 掐表：
 * - 选择器轮询有一帧以上的抖动，而 50ms / 500ms 级别的预算经不起这点噪声；
 * - **真机（iOS Safari / Web Clip standalone）跑不了 Playwright**，只能进页面读
 *   `performance.getEntriesByName(...)`。两边读同一组标记，口径才是一份。
 *
 * 标记的 `startTime` 以 `performance.timeOrigin`（≈导航开始）为基准，所以
 * 标记值本身就是「从导航开始到该里程碑的毫秒数」，不需要再减基准。
 *
 * ⚠️ 打点发生在 render 函数体内，即 DOM 提交前几十微秒、真正上屏（paint）前一帧，
 * 所以它是「主内容已决定」而不是「像素已出现」。paint 侧的数据由 e2e 从
 * `performance.getEntriesByType('paint')` 另取（WebKit 可能没有，按缺失处理）。
 *
 * 口径与预算见 docs/perf.md；标记名加 `lh:` 前缀，避免与第三方打点撞名。
 */
export const PERF_MARKS = {
  /** 首帧 DOM 已提交：main.tsx 的 render() 返回后立刻打 */
  render: 'lh:render',
  /**
   * 首屏主内容已提交 = 「可交互」判据。会话列表 / 登录页 / 消息列表三处谁先渲染
   * 出真内容算谁（深链直达聊天、未登录直达登录页都要能测到），markOnce 保证只取第一次。
   */
  interactive: 'lh:interactive',
} as const;

export type PerfMarkName = (typeof PERF_MARKS)[keyof typeof PERF_MARKS];

const marked = new Set<string>();

/**
 * 打一个标记；同名只打第一次（组件会重渲染，第二次往后必须忽略）。
 * 失败一律静默：打点是观测手段，不能把页面搞崩。
 */
export function markOnce(name: PerfMarkName): void {
  if (marked.has(name)) return;
  marked.add(name);
  try {
    performance.mark(name);
  } catch {
    // User Timing 不可用（极旧 WebView）：观测缺失可以接受，报错不行
  }
}

/** 仅供单测：重置模块级去重集合 */
export function resetPerfMarksForTest(): void {
  marked.clear();
}
